import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import SiteFooter from '../../components/feature/SiteFooter';

const packages = [
  {
    name: 'Sponsored Review',
    price: '$799',
    period: 'one-time',
    color: 'border-gray-200',
    badge: '',
    features: [
      'Full in-depth review (800–1,200 words)',
      'Honest scoring across 7 criteria',
      '"Sponsored" label for transparency',
      'Permanent placement on site',
      'Shared to 50K+ monthly readers',
      'Basic social media mention (1x)',
    ],
  },
  {
    name: 'Featured Placement',
    price: '$299',
    period: 'per month',
    color: 'border-violet-400',
    badge: 'Most Popular',
    features: [
      'Homepage "Featured Tool" section',
      'Category page top placement',
      'Logo + 2-line description',
      'Direct link to your website',
      'Monthly performance report',
      'Minimum 1-month commitment',
    ],
  },
  {
    name: 'Category Sponsorship',
    price: '$199',
    period: 'per month',
    color: 'border-gray-200',
    badge: '',
    features: [
      'Exclusive sponsor badge on 1 category',
      'Banner placement within category page',
      '"Sponsored by" label on all reviews in category',
      'Up to 3 categories available',
      'Monthly analytics report',
      'Minimum 3-month commitment',
    ],
  },
];

const stats = [
  { num: '50K+', label: 'Monthly Readers' },
  { num: '500+', label: 'Tools Reviewed' },
  { num: '8+', label: 'Tool Categories' },
  { num: '65%', label: 'Decision-Stage Visitors' },
];

export default function AdvertisePage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Advertise on 247digitrend – Reach 50K+ Decision-Makers',
    url: `${siteUrl}/advertise`,
    description:
      'Advertise your digital tool or SaaS product on 247digitrend and reach 50,000+ monthly readers who are actively comparing and choosing digital tools.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Advertise', item: `${siteUrl}/advertise` },
      ],
    },
    offers: packages.map((pkg) => ({
      '@type': 'Offer',
      name: pkg.name,
      price: pkg.price.replace('$', ''),
      priceCurrency: 'USD',
      description: pkg.features.join(', '),
    })),
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Advertise on 247digitrend – Reach 50K+ Digital Tool Buyers Monthly"
        description="Place your SaaS or AI product in front of 50,000+ decision-stage readers on 247digitrend. Sponsored reviews, featured placements, and category sponsorships available."
        canonical={`${siteUrl}/advertise`}
        keywords="advertise digital tools, SaaS advertising, sponsored review, featured placement, 247digitrend advertising"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-44 pb-20" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-megaphone-line"></i> Advertise With Us
          </span>
          <h1 className="text-5xl font-black text-gray-900 mb-5 leading-tight">
            Reach 50,000+ decision-makers<br />
            <span style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              actively choosing digital tools
            </span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8">
            Our audience comes to 247digitrend ready to buy. They&apos;re comparing tools, reading reviews, and making purchasing decisions — right now.
          </p>
          <a href="/contact" className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold px-8 py-3.5 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
            Get Custom Quote <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* STATS */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-black mb-1" style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.num}</div>
                <div className="text-sm text-gray-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ADVERTISE */}
      <section className="py-20" style={{ background: '#faf5ff' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Why Advertise on 247digitrend?</h2>
            <p className="text-sm text-gray-500">We&apos;re not just traffic — we&apos;re qualified, intent-driven readers</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-target-line', color: 'from-violet-500 to-fuchsia-500', title: 'High Purchase Intent', desc: 'Our visitors are comparing and choosing tools, not just browsing. 65% are in the decision stage when they visit.' },
              { icon: 'ri-bar-chart-2-line', color: 'from-emerald-500 to-teal-500', title: 'Niche & Qualified', desc: 'SaaS buyers, marketers, entrepreneurs, and business owners — a focused audience that actually uses the tools they find here.' },
              { icon: 'ri-shield-check-line', color: 'from-orange-400 to-pink-500', title: 'Trusted Platform', desc: 'Our editorial independence means readers trust our recommendations. Your placement is adjacent to credible content.' },
              { icon: 'ri-global-line', color: 'from-sky-500 to-cyan-500', title: 'Global Reach', desc: 'Readers from 80+ countries. Strong audiences in the US, UK, Australia, Canada, and Southeast Asia.' },
              { icon: 'ri-search-line', color: 'from-rose-400 to-fuchsia-500', title: 'SEO-Driven Traffic', desc: '72% of our traffic comes from organic search. Readers finding our content have specific tool purchase intent.' },
              { icon: 'ri-refresh-line', color: 'from-amber-400 to-orange-500', title: 'Long-Term Visibility', desc: 'Content pages stay indexed for years. Your sponsored placement continues to drive traffic long after campaign end.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${item.color} mb-4`}>
                  <i className={`${item.icon} text-white text-base`}></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Advertising Packages</h2>
            <p className="text-sm text-gray-500">Transparent pricing — no surprises, no hidden fees</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg, i) => (
              <div key={i} className={`relative rounded-2xl border-2 ${pkg.color} p-7`}>
                {pkg.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    {pkg.badge}
                  </span>
                )}
                <h3 className="text-base font-black text-gray-900 mb-1">{pkg.name}</h3>
                <div className="flex items-baseline gap-1.5 mb-5">
                  <span className="text-3xl font-black text-gray-900">{pkg.price}</span>
                  <span className="text-sm text-gray-500">/ {pkg.period}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {pkg.features.map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
                      {feat}
                    </li>
                  ))}
                </ul>
                <a href="/contact" className="block text-center bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold py-3 rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
                  Get Started
                </a>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">Need a custom solution? <a href="/contact" className="text-violet-600 font-semibold cursor-pointer hover:underline">Contact us for custom packages</a>.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #c026d3 60%, #ec4899 100%)' }}>
        <div className="max-w-2xl mx-auto px-6 text-center text-white">
          <h2 className="text-2xl font-black mb-3">Ready to get started?</h2>
          <p className="text-white/80 text-sm mb-6 leading-relaxed">Drop us an email and we&apos;ll put together a custom proposal that fits your goals and budget.</p>
          <a href="/contact" className="inline-flex items-center gap-2 bg-white text-violet-700 font-bold text-sm px-8 py-3.5 rounded-full cursor-pointer hover:bg-violet-50 transition-colors whitespace-nowrap">
            Contact Our Team <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
